import type { ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import CarIcon from '@mui/icons-material/DirectionsCar';
import LeaderIcon from '@mui/icons-material/SupervisorAccount';

import { indigo } from '@mui/material/colors';

import { useIntl } from 'react-intl';
import Tag from '~/components/Tag';
import TagAssignmentVisibility from '~/components/Tag/AssignmentVisibility';
import Typography from '@mui/material/Typography';
import { useSettingsByCategory } from '~/hooks/api/useSettingsByCategory';
import messages from './messages';
import styles from './styles.module.css';
import PublisherExperienceTag, {
  isValidExperienceLevel,
} from '../PublisherExperienceTag';
import PublisherCard from '../PublisherCard';
import Stack from '@mui/material/Stack';
import ButtonBase from '@mui/material/ButtonBase';
import { styled } from '@mui/material';

const Button = styled(ButtonBase)(({ theme }) => ({
  width: '100%',
  justifyContent: 'flex-start',
  textAlign: 'left',
  marginLeft: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
}));

type UserListItemProps = {
  application: {
    id: number;
    publisher: {
      firstName: string;
      lastName: string;
      isShiftLeader: boolean;
      isVip: boolean;
      experienceLevel: string;
      congregation: {
        name: string;
      };
      email: string;
      languages: string[];
      phone: string;
      birthDate?: string;
      gender?: string;
      id: number;
    };
    hasVehicle: boolean;
  };
  assignment?: {
    isVisible: boolean;
    isLead: boolean;
  };
  setPublisherAsLead?: () => void;
  renderAction: () => ReactNode;
  assignmentRatioNumerator: number;
  assignmentRatioDenominator: number;
};

export const UserListItem = (props: UserListItemProps) => {
  const intl = useIntl();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { data: settings } = useSettingsByCategory('planning');
  return (
    <tr className={styles.row}>
      <td>
        <PublisherCard publisher={props.application.publisher}>
          {(args) => (
            <Button onClick={args.onClick}>
              <Stack spacing={0}>
                <Typography>
                  {props.application.publisher.firstName}{' '}
                  {props.application.publisher.lastName}
                </Typography>
                <Typography variant="caption" color="GrayText">
                  {props.application.publisher.congregation.name}
                </Typography>
              </Stack>
            </Button>
          )}
        </PublisherCard>
      </td>
      {isDesktop && (
        <td>
          {props.assignmentRatioNumerator} / {props.assignmentRatioDenominator}
        </td>
      )}
      {settings?.isVehicleWorkflowActive ? (
        <td>
          {props.application.hasVehicle && (
            <CarIcon
              aria-label={intl.formatMessage(messages.hasCarAvailable)}
            />
          )}
        </td>
      ) : null}
      <td>
        {isValidExperienceLevel(props.application.publisher.experienceLevel) ? (
          <PublisherExperienceTag
            experienceLevel={props.application.publisher.experienceLevel}
          />
        ) : null}
      </td>
      <td>
        {!props.assignment && props.application.publisher.isShiftLeader && (
          <LeaderIcon aria-label={intl.formatMessage(messages.isShiftLeader)} />
        )}
        {props.assignment && props.application.publisher.isShiftLeader && (
          <Tag
            style={{
              backgroundColor: indigo[50],
              color: indigo[800],
            }}
            isInteractive
            onClick={props.setPublisherAsLead}
            left={
              props.assignment && props.assignment.isLead ? (
                <CheckCircleIcon
                  style={{
                    height: '16px',
                    width: 'auto',
                    color: indigo[800],
                  }}
                />
              ) : (
                <CheckCircleIcon
                  style={{
                    height: '16px',
                    width: 'auto',
                    color: indigo[100],
                  }}
                />
              )
            }
          >
            {intl.formatMessage(messages.leader)}
          </Tag>
        )}
      </td>
      {isDesktop && (
        <>
          <td>
            {props.application.publisher.isVip && (
              <StarIcon aria-label={intl.formatMessage(messages.vip)} />
            )}
          </td>
          <td>
            {props.assignment && props.assignment.isVisible && (
              <TagAssignmentVisibility
                aria-label={intl.formatMessage(messages.assignmentIsVisible)}
              />
            )}
          </td>
        </>
      )}
      <td>{props.renderAction()}</td>
    </tr>
  );
};
export default UserListItem;
